// Libs
import classNames from 'classnames/bind';
import { Button, Flex, Heading, IconRefreshLine, Spinner, Text, TextInput } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
// Components, Layouts, Pages
import CourseTable from '../courseTable/CourseTable';
import BaseSelect from '~/components/common/specific/baseSelect/BaseSelect';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { useCanvasSessionExpired } from '~/utils/hooks/canvasSession';
import { studentRoute } from '~/utils/routes/student';
import { IModule } from '~/utils/interface/course';
import { getCourseUpcoming } from '~/thunks/course/courseThunk';
import { LessonStatusFilterEnum, LessonTypeFilterEnum } from '~/utils/enum/common';
import { COURSE_ACTIVATION_MESSAGE_KEYS, LESSON_TYPE_FILTER_KEYWORDS } from '~/utils/constants/common';
// Styles, images, icons
import styles from './AvailableLesson.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const AvailableLesson = (props: Props) => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'common/course']);
  const dispatch = useAppDispatch();
  const isCanvasSessionExpired = useCanvasSessionExpired();
  //#endregion Declare Hook

  //#region Selector
  const { availableLessons, isLoading, errorMessage, errorCode } = useAppSelector((state) => state.courseState);
  // Course not active yet or no course: explain it instead of offering Retry
  const activationMessageKey = errorCode ? COURSE_ACTIVATION_MESSAGE_KEYS[errorCode] : undefined;
  const navigate = useNavigate();
  //#endregion Selector

  //#region Declare State
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<LessonStatusFilterEnum>(LessonStatusFilterEnum.ALL);
  const [typeFilter, setTypeFilter] = useState<LessonTypeFilterEnum>(LessonTypeFilterEnum.ALL);
  //#endregion Declare State

  //#region Declare Memo
  const filteredLessons = useMemo(() => {
    return availableLessons.filter((lesson) => {
      const name = (lesson.name ?? '').trim().toLowerCase();

      if (searchText && !name.includes(searchText.trim().toLowerCase())) return false;

      if (statusFilter === LessonStatusFilterEnum.WATCHED && !lesson.isWatched) return false;
      if (statusFilter === LessonStatusFilterEnum.UNWATCHED && lesson.isWatched) return false;

      if (typeFilter !== LessonTypeFilterEnum.ALL) {
        const hasKeyword = (type: LessonTypeFilterEnum) =>
          LESSON_TYPE_FILTER_KEYWORDS[type].some((kw) => name.includes(kw));
        // A forum is recognised by its content, whatever its name ("DISCUSSION 1"); the name keywords stay as a fallback.
        const isForum = lesson.hasDiscussionForum || hasKeyword(LessonTypeFilterEnum.DISCUSSION_FORUM);
        const isMidterm = hasKeyword(LessonTypeFilterEnum.MIDTERM_EXAM);
        const isFinal = hasKeyword(LessonTypeFilterEnum.FINAL_EXAM);
        // Lessons have no common name ("LESSON 1.1", "WEEK 1.1"): anything that is neither a forum nor an exam counts.
        const isLesson = hasKeyword(LessonTypeFilterEnum.LESSON) || (!isForum && !isMidterm && !isFinal);
        const matchesType: Record<string, boolean> = {
          [LessonTypeFilterEnum.DISCUSSION_FORUM]: isForum,
          [LessonTypeFilterEnum.MIDTERM_EXAM]: isMidterm,
          [LessonTypeFilterEnum.FINAL_EXAM]: isFinal,
          [LessonTypeFilterEnum.LESSON]: isLesson,
        };
        if (!matchesType[typeFilter]) return false;
      }

      return true;
    });
  }, [availableLessons, searchText, statusFilter, typeFilter]);

  const statusOptions = useMemo(
    () => [
      { value: LessonStatusFilterEnum.ALL, label: t('available_lesson_filter_status_all') },
      { value: LessonStatusFilterEnum.UNWATCHED, label: t('available_lesson_filter_status_unwatched') },
      { value: LessonStatusFilterEnum.WATCHED, label: t('available_lesson_filter_status_watched') },
    ],
    [t]
  );

  const typeOptions = useMemo(
    () => [
      { value: LessonTypeFilterEnum.ALL, label: t('available_lesson_filter_type_all') },
      { value: LessonTypeFilterEnum.DISCUSSION_FORUM, label: t('available_lesson_filter_type_discussion_forum') },
      { value: LessonTypeFilterEnum.MIDTERM_EXAM, label: t('available_lesson_filter_type_midterm_exam') },
      { value: LessonTypeFilterEnum.FINAL_EXAM, label: t('available_lesson_filter_type_final_exam') },
      { value: LessonTypeFilterEnum.LESSON, label: t('available_lesson_filter_type_lesson') },
    ],
    [t]
  );
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleClickCourseItem = (courseId?: string, moduleId?: string) => {
    navigate(`${studentRoute.base}${studentRoute.course}/${courseId}${studentRoute.module}/${moduleId}`);
  };

  const handleGetListCourses = async () => {
    await dispatch(getCourseUpcoming()).unwrap();
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage && !activationMessageKey) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center'>
            {isCanvasSessionExpired ? (
              <Text variant='contentSmall'>{t('canvas_session_expired_card_message', { ns: 'common/base' })}</Text>
            ) : (
              <Button onClick={handleGetListCourses} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
                {t('retry_button_label', { ns: 'common/base' })}
              </Button>
            )}
          </Flex>
        </div>
      );
    }

    return (
      <>
        <div className={cx('header')}>
          <Heading level='h3'>{t('study_courses_available_lessons_title')}</Heading>

          <Text variant='contentSmall'>
            {availableLessons.length > 1
              ? t('study_courses_available_plural', { ns: 'common/home', count: availableLessons.length })
              : t('study_courses_available', { ns: 'common/home', count: availableLessons.length })}
          </Text>
        </div>

        <div className={cx('filterBar')}>
          <TextInput
            renderLabel=''
            placeholder={t('available_lesson_search_placeholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            display='inline-block'
          />

          <BaseSelect
            value={statusFilter}
            options={statusOptions}
            onChange={(option) =>
              setStatusFilter((option?.value ?? LessonStatusFilterEnum.ALL) as LessonStatusFilterEnum)
            }
          />

          <BaseSelect
            value={typeFilter}
            options={typeOptions}
            onChange={(option) => setTypeFilter((option?.value ?? LessonTypeFilterEnum.ALL) as LessonTypeFilterEnum)}
          />
        </div>

        {filteredLessons?.length > 0 ? (
          <CourseTable
            courses={filteredLessons}
            onClick={(module: IModule) => handleClickCourseItem(module.courseId, module.id)}
          />
        ) : (
          <div className={cx('centerContainer')}>
            <Flex justifyItems='center' alignItems='center'>
              <Text variant='contentSmall'>
                {t(activationMessageKey ?? 'study_courses_program_no_lesson_available', { ns: 'common/home' })}
              </Text>
            </Flex>
          </div>
        )}
      </>
    );
  };
  //#endregion Handle Function

  return <>{renderContent()}</>;
};

export default AvailableLesson;
